import { forwardRef, memo, useMemo, useRef, useState } from 'react';
import { CalendarIcon } from '@page/icons';
import { DatePicker } from '@mantine/dates';

import { formatDate, formatRequestDate, parseDateOnly } from '@page/core/utils/date';
import { Input, InputProps } from '../../designSystem/Input';
import { cn } from '../../lib/styles';
import { Popover, PopoverContent, PopoverContentProps, PopoverTrigger } from '../../designSystem/Popover';
import { IconButton } from '../../designSystem/IconButton';

interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  value?: string;
  textValue?: string;
  onChange?: (date: string, fromDatePicker: boolean) => void;
  size?: InputProps['size'];
  label?: string;
  containerClassName?: string;
  inputClassName?: string;
  rightIcon?: React.ReactNode;
  datePickerDefaultOpen?: boolean;
  alwaysShowValue?: boolean;
  datePopoverContentProps?: PopoverContentProps;
}

const DateInputBase = (
  {
    value,
    textValue,
    onChange,
    className,
    label,
    id,
    min,
    max,
    disabled,
    placeholder,
    onFocus,
    onBlur,
    containerClassName,
    inputClassName,
    rightIcon,
    datePickerDefaultOpen,
    alwaysShowValue = false,
    datePopoverContentProps,
    ...rest
  }: DateInputProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const actualId = id || rest.name;

  const [_type, setType] = useState<'date' | 'text'>(placeholder ? 'text' : 'date');
  const [error, setError] = useState<string | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(datePickerDefaultOpen ?? false);
  const initialValue = useRef(value || '');

  const datePickerValue = useMemo(() => (value ? parseDateOnly(value) : undefined), [value]);
  const minDate = useMemo(() => (min ? new Date(min) : undefined), [min]);
  const maxDate = useMemo(() => (max ? new Date(max) : undefined), [max]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const date = parseDateOnly(event.target.value);

    if (minDate && date < minDate) {
      setError(['Date cannot be before', formatDate(minDate)].join(' '));
      onChange?.(initialValue.current, false);
      return;
    }

    if (maxDate && date > maxDate) {
      setError(['Date cannot be after', formatDate(maxDate)].join(' '));
      onChange?.(initialValue.current, false);
      return;
    }

    setError(null);
    onChange?.(event.target.value, false);
  };

  const handleDatePickerChange = (date: Date | null) => {
    if (!date) return;

    onChange?.(formatRequestDate(date), true);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (_type === 'text') setType('date');

    if (!value) {
      setPopoverOpen(true);
    }

    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (placeholder) setType('text');

    onBlur?.(event);
  };

  const type = alwaysShowValue ? 'date' : _type;

  return (
    <Input
      id={actualId}
      ref={ref}
      type={type}
      label={label}
      value={type === 'text' ? textValue ?? value : value}
      onChange={handleInputChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      min={min}
      max={max}
      error={error}
      containerClassName={containerClassName}
      className={className}
      inputClassName={cn(
        '[&::-webkit-calendar-picker-indicator]:hidden focus-within:outline-border-input-select',
        inputClassName,
        {
          'cursor-pointer': type === 'text',
        },
      )}
      rightIconClassName="pointer-events-auto right-1"
      disabled={disabled}
      rightIcon={
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <IconButton size="sm" disabled={disabled}>
              {rightIcon ?? <CalendarIcon className="size-5 min-w-5 cursor-pointer" />}
            </IconButton>
          </PopoverTrigger>

          <PopoverContent
            withinPortal
            {...datePopoverContentProps}
            className={cn('date-input__date-picker-popover', datePopoverContentProps?.className)}
          >
            <DatePicker
              defaultDate={datePickerValue}
              value={datePickerValue}
              onChange={handleDatePickerChange}
              minDate={minDate}
              maxDate={maxDate}
            />
          </PopoverContent>
        </Popover>
      }
      {...rest}
    />
  );
};

export const DateInput = memo(forwardRef(DateInputBase));
